import React, { useState, useEffect } from 'react';
import { DataProvider, useData } from './context/DataContext';
import { FormatProvider, useFormat } from './context/FormatContext';
import FormatProDashboard from './components/FormatProDashboard';
import FormatProPipeline from './components/FormatProPipeline';
import AdminUserManagement from './components/AdminUserManagement';
import SafeIcon from './common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiLoader, FiAlertCircle, FiArrowRight, FiRefreshCw } = FiIcons;

const AppContent = () => {
  const { pipelineState } = useFormat();
  const { loading, authError, currentUser, retryAuth } = useData();
  const [isAdminView, setIsAdminView] = useState(() => {
    return window.location.hash.toLowerCase().includes('admin');
  });

  const isAdmin = (currentUser?.access_type || '').toLowerCase() === 'admin';

  useEffect(() => {
    const handleHash = () => {
      const isHashAdmin = window.location.hash.toLowerCase().includes('admin');
      setIsAdminView(isHashAdmin);
    };
    window.addEventListener('hashchange', handleHash);
    return () => window.removeEventListener('hashchange', handleHash);
  }, []);

  const openAdmin = () => {
    window.location.hash = '#/admin';
    setIsAdminView(true);
  };

  const closeAdmin = () => {
    window.location.hash = '';
    setIsAdminView(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-cream flex flex-col items-center justify-center">
        <SafeIcon icon={FiLoader} className="text-4xl text-primary animate-spin mb-4" />
        <p className="text-slate-500 font-medium animate-pulse">Establishing Secure Connection...</p>
      </div>
    );
  }

  if (authError && !currentUser) {
    return (
      <div className="min-h-screen bg-cream flex flex-col items-center justify-center p-6 text-center">
        <div className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center mb-6">
          <SafeIcon icon={FiAlertCircle} className="text-3xl text-red-600" />
        </div>
        <h1 className="text-2xl font-serif text-slate-800 mb-2">Authentication Notice</h1>
        <p className="text-slate-500 mb-6 max-w-sm text-sm">{authError}</p>
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={() => {
              sessionStorage.setItem('gospelwise_user', JSON.stringify({
                id: 1,
                local_id: 1,
                hub_id: 'GWA-DEMO',
                name: 'Author Admin',
                email: 'admin@gospelwiseauthor.app',
                access_type: 'admin',
                single_amt: 5
              }));
              window.location.reload();
            }}
            className="px-5 py-2.5 bg-primary text-white text-xs font-bold uppercase tracking-wider rounded-xl shadow-sm hover:opacity-90 flex items-center justify-center space-x-2"
          >
            <span>Continue in Preview Mode</span>
            <SafeIcon icon={FiArrowRight} />
          </button>
          {retryAuth && (
            <button
              onClick={() => retryAuth()}
              className="px-5 py-2.5 bg-white border border-slate-200 text-slate-700 text-xs font-bold uppercase tracking-wider rounded-xl hover:bg-slate-50 flex items-center justify-center space-x-2"
            >
              <SafeIcon icon={FiRefreshCw} className="text-xs" />
              <span>Retry Token</span>
            </button>
          )}
          <a
            href="https://www.gospelwiseauthor.app/#/login"
            className="px-5 py-2.5 bg-white border border-slate-200 text-slate-700 text-xs font-bold uppercase tracking-wider rounded-xl hover:bg-slate-50 flex items-center justify-center"
          >
            Go to GospelWise Hub
          </a>
        </div>
      </div>
    );
  }

  if (!currentUser) {
    return (
      <div className="min-h-screen bg-cream flex flex-col items-center justify-center p-6">
        <SafeIcon icon={FiLoader} className="text-3xl text-primary animate-spin mb-3" />
        <p className="text-slate-500 text-sm">Preparing FormatterPro Workspace...</p>
      </div>
    );
  }

  // Admin Module Route Check
  if (isAdminView && isAdmin) {
    return <AdminUserManagement onBack={closeAdmin} />;
  }

  if (pipelineState) {
    return <FormatProPipeline />;
  }
  
  return <FormatProDashboard onOpenAdmin={openAdmin} />;
};

function App() {
  return (
    <DataProvider>
      <FormatProvider>
        <AppContent />
      </FormatProvider>
    </DataProvider>
  );
}

export default App;